import { observer } from 'mobx-react-lite';
import { Loader2, ArrowRightLeft, AlertCircle, X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { OwnerModal } from '@/controllers/clients/clientCard/useOwnerModal';

type Props = { ctrl: OwnerModal };

export const OwnerTransferModal = observer(function OwnerTransferModal({ ctrl }: Props) {
  return (
    <Modal
      isOpen={ctrl.isOpen}
      onClose={ctrl.close}
      title='Передать камеру'
      subtitle={ctrl.cameraName}
      size='md'
      footer={
        <>
          <Button variant='outline' onClick={ctrl.close} className='flex-1'>
            Отмена
          </Button>
          <Button
            onClick={ctrl.submit}
            disabled={!ctrl.selectedAccount || ctrl.isLoading}
            className='flex-1 gap-2'
          >
            {ctrl.isLoading ? (
              <Loader2 className='w-4 h-4 animate-spin' />
            ) : (
              <ArrowRightLeft className='w-4 h-4' />
            )}
            Передать
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <div>
          <Label className='text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5'>
            Аккаунт получателя
          </Label>

          {ctrl.selectedAccount ? (
            <div className='flex items-center justify-between px-3.5 py-2.5 rounded-xl border border-primary/50 bg-primary/5 text-sm'>
              <div>
                <span className='font-medium text-foreground'>{ctrl.selectedAccount.login}</span>
                <span className='text-muted-foreground ml-2'>
                  {ctrl.selectedAccount.account_name}
                </span>
              </div>
              <button
                onClick={ctrl.clearSelection}
                className='text-muted-foreground hover:text-foreground transition-colors ml-2 shrink-0'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
          ) : (
            <div className='relative'>
              <Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
              <Input
                type='text'
                placeholder='Поиск по логину'
                value={ctrl.searchQuery}
                onChange={(e) => ctrl.setSearchQuery(e.target.value)}
                className='pl-9 bg-muted/30 border-border/50 focus-visible:border-primary/50 focus-visible:ring-0'
                autoFocus
              />
              {(ctrl.isSearching || ctrl.searchResults.length > 0) && (
                <div className='absolute z-10 mt-1 w-full rounded-xl border border-border/50 bg-card shadow-lg overflow-hidden'>
                  {ctrl.isSearching ? (
                    <div className='flex items-center gap-2 px-3.5 py-2.5 text-sm text-muted-foreground'>
                      <Loader2 className='w-3.5 h-3.5 animate-spin' />
                      Поиск...
                    </div>
                  ) : ctrl.searchResults.length === 0 ? (
                    <div className='px-3.5 py-2.5 text-sm text-muted-foreground'>
                      Ничего не найдено
                    </div>
                  ) : (
                    ctrl.searchResults.map((account) => (
                      <button
                        key={account.id_account}
                        onClick={() => ctrl.selectAccount(account)}
                        className='w-full text-left px-3.5 py-2.5 text-sm hover:bg-muted/40 transition-colors border-b border-border/20 last:border-0'
                      >
                        <span className='font-medium text-foreground'>{account.login}</span>
                        <span className='text-muted-foreground ml-2'>{account.account_name}</span>
                        <span className='text-muted-foreground/60 ml-2 text-xs'>
                          {account.country_name}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <Label className='flex items-center gap-3 cursor-pointer select-none'>
          <div
            onClick={() => ctrl.setMoveInVc(!ctrl.moveInVc)}
            className={cn(
              'w-9 h-5 rounded-full transition-colors relative shrink-0',
              ctrl.moveInVc ? 'bg-primary' : 'bg-muted/60 border border-border/50'
            )}
          >
            <div
              className={cn(
                'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all',
                ctrl.moveInVc ? 'left-[calc(100%-1.125rem)]' : 'left-0.5'
              )}
            />
          </div>
          <span className='text-sm text-foreground'>Перенести в VC</span>
        </Label>

        {ctrl.error && (
          <div className='flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-xl text-destructive text-sm'>
            <AlertCircle className='w-4 h-4 shrink-0' />
            {ctrl.error}
          </div>
        )}
      </div>
    </Modal>
  );
});
